import { api } from "@/lib/apiClient";
import { isAxiosError } from "axios";

export type SaleIndexRecord = {
  id: number;
  invoice_no?: string;
  product_id: number | null;
  customer_id: number | null;
  employee_id: number | null;
  broker_party_id?: number | null;
  broker_commission?: number | null;
  broker_commission_payer?: "seller" | "showroom" | null;
  broker_commission_paid?: number | null;
  broker_commission_outstanding?: number | null;
  broker?: { id: number; name: string; phone?: string | null } | null;
  date: string | null;
  total_amount: number;
  token_amount?: number | null;
  token_type?: string | null;
  balance_amount?: number | null;
  is_installment?: boolean;
  installment_count?: number | null;
  balance_paid_total?: number | null;
  balance_outstanding?: number | null;
  paid_total?: number | null;
  outstanding?: number | null;
  payment_status?: "unpaid" | "partial" | "paid" | "cancelled" | "returned" | null;
  cancelled_at?: string | null;
  is_cancelled?: boolean;
  is_returned?: boolean;
  void_type?: "cancel" | "return" | string | null;
  sale_return?: {
    id: number;
    return_date?: string | null;
    sale_amount: number;
    collected_amount: number;
    refunded_amount: number;
    retained_amount: number;
    notes?: string | null;
  } | null;
  payable_date?: string | null;
  payment_method?: string | null;
  bank_id?: number | null;
  bank_name?: string | null;
  transaction_reference_no?: string | null;
  pay_order_no?: string | null;
  description: string | null;
  created_at: string | null;
  updated_at: string | null;
  product?: { id: number; name: string; image_url?: string | null } | null;
  customer?: { id: number; name: string; phone?: string | null } | null;
  employee?: { id: number; full_name?: string | null; employee_code?: string | null } | null;
  payments?: Array<{
    id?: number;
    amount?: number;
    transaction_type?: string | null;
    payment_date?: string | null;
    payment_method?: string | null;
    bank_id?: number | null;
    bank_name?: string | null;
    bank?: { name?: string | null } | null;
    transaction_reference_no?: string | null;
    pay_order_no?: string | null;
    cheque_no?: string | null;
    deposit_slip_no?: string | null;
    cheque_date?: string | null;
    notes?: string | null;
  }>;
};

export type SalesIndexApiResponse = {
  data?: SaleIndexRecord[];
  meta?: { current_page: number; last_page: number; per_page: number; total: number };
};

export type SalesIndexFilters = {
  page?: number;
  per_page?: number;
  date_from?: string | null;
  date_to?: string | null;
  product_id?: number | null;
  customer_id?: number | null;
  employee_id?: number | null;
  scope?: "returnable" | "returned" | "active" | null;
};

const SALES_PER_PAGE = 100;

function readLastPage(body: unknown): number | null {
  if (!body || typeof body !== "object") return null;
  const meta = (body as { meta?: { last_page?: number } }).meta;
  return meta?.last_page ?? null;
}

export function salesIndexErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;
    if (data?.errors) {
      const first = Object.values(data.errors).flat()[0];
      if (first) return first;
    }
    if (typeof data?.message === "string") return data.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return "Something went wrong. Please try again.";
}

export async function fetchSalesIndex(filters: SalesIndexFilters): Promise<SalesIndexApiResponse> {
  const response = await api.get<SalesIndexApiResponse>("/api/v1/sales", {
    params: {
      page: filters.page ?? 1,
      per_page: filters.per_page ?? 20,
      date_from: filters.date_from ?? null,
      date_to: filters.date_to ?? null,
      product_id: filters.product_id ?? null,
      customer_id: filters.customer_id ?? null,
      employee_id: filters.employee_id ?? null,
      scope: filters.scope ?? null,
    },
  });
  return response.data ?? {};
}

export async function syncSalesFromApi(): Promise<SaleIndexRecord[]> {
  const collected: SaleIndexRecord[] = [];
  let page = 1;
  let lastPage = 1;
  do {
    const res = await fetchSalesIndex({ page, per_page: SALES_PER_PAGE });
    collected.push(...(res.data ?? []));
    lastPage = res.meta?.last_page ?? readLastPage(res) ?? 1;
    page += 1;
  } while (page <= lastPage && page <= 50);

  return collected;
}

export async function fetchSaleFromApi(id: string | number): Promise<SaleIndexRecord | null> {
  const response = await api.get<{ data?: SaleIndexRecord }>(`/api/v1/sales/${id}`);
  return response.data?.data ?? null;
}
